import React from 'react';
import styled, { css } from 'styled-components';

import { orbitTokens, orbitType } from '../styles';

export interface IOrbitActionChipProps {
  label: string;
  onSelect?: () => void;
  disabled?: boolean;
  testId?: string;
}

const CHIP_WIDTH = 219.5;
const CHIP_GAP = 8;

/**
 * div.action-chip-row `4955:11274`. The chip is fixed at 219.5 rather than stretched because two of
 * them plus this gap fill the 447 content column exactly (219.5 + 8 + 219.5).
 */
export const OrbitActionChipRow = styled.div`
  display: flex;
  padding-top: 8px;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: ${CHIP_GAP}px;
  }
`;

// Component 1 `4955:11275`, a different shape from the suggested-action row: a 34-tall r20 pill on
// the panel surface with centred text, not a filled r10 row.
const chipRules = css`
  display: flex;
  /* 219.5 + 8 + 219.5 fills the 447 column exactly, so the basis is the drawn width; it may shrink
     rather than overflow if a host ever gives the row less. */
  flex: 0 1 ${CHIP_WIDTH}px;
  min-width: 0;
  align-items: center;
  justify-content: center;
  height: 34px;
  margin: 0;
  padding: 9px 14px;
  background: ${orbitTokens.surface};
  border: 1px solid ${orbitTokens.border};
  border-radius: 20px;
  color: ${orbitTokens.textSecondary};
  font-family: inherit;
  ${orbitType.chip}
  font-weight: 400;
  text-align: center;
`;

const Chip = styled.div`
  ${chipRules}
`;

const ChipButton = styled.button`
  ${chipRules}
  cursor: pointer;

  /* figma-spec-v3.md section 4c: the hover variant of 4955:11275 is 4952:5001, which changes stroke
     and ink only. A real :hover rule, so no React state stands behind it. */
  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${orbitTokens.action};
    color: ${orbitTokens.text};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const OrbitActionChip: React.FC<IOrbitActionChipProps> = ({
  label,
  onSelect,
  disabled = false,
  testId
}) =>
  onSelect === undefined ? (
    <Chip data-testid={testId}>{label}</Chip>
  ) : (
    <ChipButton type="button" disabled={disabled} data-testid={testId} onClick={onSelect}>
      {label}
    </ChipButton>
  );

export default OrbitActionChip;
